import React from 'react';
import {
  TrendingUp,
  LocationOn,
  Agriculture,
  Store,
  Shield,
  BarChart,
  PieChart,
  Warning,
} from '@mui/icons-material';
import { formatCurrency } from './KPISummaryCards';

/**
 * A metric card for the analytics grid.
 */
const AnalyticsCard = ({ icon, title, value, subtitle, color, trend }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-4 hover:shadow-sm transition-shadow">
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 16, color } })}
        </div>
        <span className="text-xs font-medium text-gray-500">{title}</span>
      </div>
      {trend !== undefined && (
        <span className={`text-xs font-semibold ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend >= 0 ? '↑' : '↓'}{Math.abs(trend)}%
        </span>
      )}
    </div>
    <p className="text-xl font-bold text-gray-900">{value}</p>
    {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
  </div>
);

/**
 * A bar chart row (simplified horizontal bar).
 */
const HorizontalBar = ({ label, value, maxValue, color, suffix = '' }) => {
  const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <div className="py-2">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-gray-600 truncate max-w-[60%]">{label}</span>
        <span className="font-semibold text-gray-800">
          {typeof value === 'number' && value >= 1000 ? formatCurrency(value) : value}
          {suffix}
        </span>
      </div>
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

/**
 * RiskAnalyticsTab
 *
 * Comprehensive risk analytics dashboard with:
 *   - Portfolio health metrics
 *   - District exposure breakdown
 *   - Crop cycle analysis
 *   - Vendor payment distribution
 */
const RiskAnalyticsTab = ({ data = {}, loading = false }) => {
  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-100 rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-48 bg-gray-100 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  // Portfolio health metrics
  const portfolioMetrics = [
    {
      icon: <Shield />,
      title: 'Portfolio Health',
      value: data.portfolioHealth ?? 'Good',
      subtitle: `NPA: ${(data.npaRiskPercent ?? 0).toFixed(1)}%`,
      color: (data.npaRiskPercent ?? 0) > 10 ? '#ef4444' : '#10b981',
    },
    {
      icon: <BarChart />,
      title: 'Avg Loan Size',
      value: formatCurrency(data.avgLoanSize ?? 0),
      subtitle: `Across ${data.totalLoans ?? 0} loans`,
      color: '#6366f1',
    },
    {
      icon: <PieChart />,
      title: 'Collection Rate',
      value: `${(data.collectionRate ?? 0).toFixed(1)}%`,
      subtitle: 'On-time repayments',
      color: '#0ea5e9',
      trend: data.collectionTrend,
    },
    {
      icon: <TrendingUp />,
      title: 'Avg Cycle Duration',
      value: `${data.avgCycleDays ?? 0} days`,
      subtitle: 'Stocking to settlement',
      color: '#f59e0b',
    },
  ];

  // District exposure data
  const districtExposure = data.districtExposure || [
    { district: 'Balasore', exposure: 2500000, farmers: 12 },
    { district: 'Bhadrak', exposure: 1800000, farmers: 8 },
    { district: 'Kendrapara', exposure: 1200000, farmers: 6 },
    { district: 'Jagatsinghpur', exposure: 900000, farmers: 4 },
    { district: 'Puri', exposure: 600000, farmers: 3 },
  ];
  const maxDistrictExposure = Math.max(...districtExposure.map((d) => d.exposure), 1);

  // Crop type distribution
  const cropDistribution = data.cropDistribution || [
    { crop: 'Vannamei Shrimp', count: 45, value: 4500000 },
    { crop: 'Rohu', count: 28, value: 1800000 },
    { crop: 'Catla', count: 15, value: 900000 },
    { crop: 'Pangasius', count: 12, value: 750000 },
  ];
  const maxCropValue = Math.max(...cropDistribution.map((c) => c.value), 1);

  // Vendor payment distribution
  const vendorPayments = data.vendorPayments || [
    { vendor: 'AquaFeed Corp', amount: 1200000, percentage: 35 },
    { vendor: 'FishSeed Suppliers', amount: 800000, percentage: 23 },
    { vendor: 'Nutrifish Ltd', amount: 600000, percentage: 17 },
    { vendor: 'PondCare Services', amount: 450000, percentage: 13 },
    { vendor: 'Others', amount: 400000, percentage: 12 },
  ];
  const maxVendorAmount = Math.max(...vendorPayments.map((v) => v.amount), 1);

  // Risk indicators
  const riskIndicators = data.riskIndicators || [
    { label: 'Overdue Applications', count: data.overdueApplications ?? 0, severity: 'high' },
    { label: 'Approaching Default', count: data.defaultRiskCount ?? 0, severity: 'high' },
    { label: 'Stale Escrow (>30d)', count: data.staleEscrowCount ?? 0, severity: 'medium' },
    { label: 'Missing Harvest Report', count: data.missingHarvestCount ?? 0, severity: 'medium' },
    { label: 'Incomplete Settlement', count: data.incompleteSettlementCount ?? 0, severity: 'low' },
  ];

  return (
    <div className="space-y-6">
      {/* Portfolio Health Metrics */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Shield sx={{ fontSize: 16, color: '#6366f1' }} />
          Portfolio Health Metrics
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {portfolioMetrics.map((m, i) => (
            <AnalyticsCard key={i} {...m} />
          ))}
        </div>
      </div>

      {/* Charts / Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* District Exposure */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="flex items-center gap-2 mb-4">
            <LocationOn sx={{ fontSize: 18, color: '#6366f1' }} />
            <h4 className="text-sm font-semibold text-gray-700">District Exposure</h4>
          </div>
          <div className="space-y-1">
            {districtExposure.map((d, i) => (
              <HorizontalBar
                key={i}
                label={`${d.district} (${d.farmers} farmers)`}
                value={d.exposure}
                maxValue={maxDistrictExposure}
                color={['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444'][i % 5]}
              />
            ))}
          </div>
        </div>

        {/* Crop Distribution */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="flex items-center gap-2 mb-4">
            <Agriculture sx={{ fontSize: 18, color: '#10b981' }} />
            <h4 className="text-sm font-semibold text-gray-700">Credit by Crop Type</h4>
          </div>
          <div className="space-y-1">
            {cropDistribution.map((c, i) => (
              <HorizontalBar
                key={i}
                label={`${c.crop} (${c.count} cases)`}
                value={c.value}
                maxValue={maxCropValue}
                color={['#10b981', '#0ea5e9', '#6366f1', '#f59e0b'][i % 4]}
              />
            ))}
          </div>
        </div>

        {/* Vendor Payments */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="flex items-center gap-2 mb-4">
            <Store sx={{ fontSize: 18, color: '#f59e0b' }} />
            <h4 className="text-sm font-semibold text-gray-700">Vendor Payment Distribution</h4>
          </div>
          <div className="space-y-1">
            {vendorPayments.map((v, i) => (
              <HorizontalBar
                key={i}
                label={`${v.vendor} (${v.percentage}%)`}
                value={v.amount}
                maxValue={maxVendorAmount}
                color={v.percentage > 30 ? '#ef4444' : ['#f59e0b', '#0ea5e9', '#10b981', '#6366f1', '#8b5cf6'][i % 5]}
              />
            ))}
          </div>
          {vendorPayments.some((v) => v.percentage > 30) && (
            <div className="mt-3 flex items-center gap-1 text-xs text-red-500">
              <Warning sx={{ fontSize: 14 }} />
              Vendor concentration risk detected ({'>'}30% to single vendor)
            </div>
          )}
        </div>

        {/* Risk Indicators */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="flex items-center gap-2 mb-4">
            <Warning sx={{ fontSize: 18, color: '#ef4444' }} />
            <h4 className="text-sm font-semibold text-gray-700">Risk Indicators</h4>
          </div>
          <div className="space-y-3">
            {riskIndicators.map((r, i) => {
              const severityColors = {
                high: { bg: 'bg-red-50', border: 'border-red-100', text: 'text-red-700', dot: 'bg-red-500' },
                medium: { bg: 'bg-amber-50', border: 'border-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
                low: { bg: 'bg-blue-50', border: 'border-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
              };
              const cfg = severityColors[r.severity] || severityColors.low;
              return (
                <div
                  key={i}
                  className={`flex items-center justify-between p-3 rounded-xl ${cfg.bg} ${cfg.border} border`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                    <span className="text-sm text-gray-700">{r.label}</span>
                  </div>
                  <span className={`text-sm font-bold ${cfg.text}`}>{r.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskAnalyticsTab;
